import { z } from "zod"
import { formatUSPhone, phoneRegex } from "@/app/utils/index"

const strongPasswordRegex = /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/

const passwordSchema = z
  .string()
  .min(8, { message: "Password must be between 8 and 100 characters" })
  .max(100, { message: "Password must be between 8 and 100 characters" })
  .regex(strongPasswordRegex, {
    message:
      "Password must contain upper & lower case letters and at least 1 number or special character",
  })

export const authSchemas = {
  register: z.object({
    name: z
      .string()
      .trim()
      .min(3, { message: "Name must be at least 3 characters" })
      .max(50, { message: "Name must be less than 50 characters" })
      .refine((value) => value.split(" ").filter(Boolean).length >= 2, {
        message: "Please enter your full name (first and last name)",
      }),
    password: passwordSchema,
    email: z.string().trim().email({ message: "Please enter a valid email address" }),
    phoneNumber: z.preprocess(
      formatUSPhone,
      z
        .string({
          required_error: "Phone number is required",
          invalid_type_error: "Phone number is required",
        })
        .refine((val) => phoneRegex.test(val), {
          message: "Please enter a valid US/Canada phone number",
        })
    ),
  }),
  login: z.object({
    contact: z.preprocess(
      (val) => {
        if (typeof val !== "string") return val
        if (val.includes("@")) return val
        return formatUSPhone(val)
      },
      z.string().refine((val) => {
        if (val.includes("@")) {
          return z.string().email().safeParse(val).success
        }
        return phoneRegex.test(val)
      }, {
        message: "Please enter a valid email or US/Canada phone number",
      })
    ),
    password: passwordSchema,
  }),
  forgotPassword: z.object({
    contact: z.preprocess(
      (val) => {
        if (typeof val !== "string") return val
        if (val.includes("@")) return val
        return formatUSPhone(val)
      },
      z.string().refine((val) => {
        if (val.includes("@")) {
          return z.string().email().safeParse(val).success
        }
        return phoneRegex.test(val)
      }, {
        message: "Please enter a valid email or US/Canada phone number",
      })
    ),
  }),
  resetPassword: z
    .object({
      password: passwordSchema,
      confirmPassword: z
        .string()
        .min(8, { message: "Password must be between 8 and 100 characters" })
        .max(100, { message: "Password must be between 8 and 100 characters" }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    }),
}